import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IAccountUserMapping, IUser, UserPlatform } from "@interfaces/IUser";
import { AccountService } from "@services/account.service";
import { AccountUserMappingService } from "@app/services/accountUserMapping.service.ajs";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
//import * as rg4js from 'raygun4js';
import { SettingsName } from "../class/SettingsName";
import { UserType } from "@enums/UserType";
import { CacheService } from "./cache.Service";
import { SessionService } from "./session.service";
import { UserResellerMappingService } from "./userResellerMappingService.ajs";
import { IResellerAccountMapping } from "./resellerAccountMapping.service";
import { IReseller } from "@app/main/modules/foundation/_models/IReseller";

export class CurrentUserService {

    static CurrentUserFeaturesUpdatedEvent = "CurrentUserFeaturesUpdatedEvent";

    cachedUser: IUser;

    private cacheName = "currentUserCache";

    static $inject = ["$q", "$http", "$rootScope", "$timeout", "$mdDialog", "cacheService", "accountService", "accountUserMappingService", "translationService","sessionService", "userResellerMappingService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly cacheService: CacheService,
        private readonly accountService: AccountService,
        private readonly accountUserMappingService: AccountUserMappingService,
        private readonly translationService: TranslationService,
        private readonly sessionService: SessionService,
        private readonly userResellerMappingService: UserResellerMappingService
    ) {
        this.cacheService.createCache(this.cacheName);
    }

    getCurrentUserPlatform(): UserPlatform {
        return this.cachedUser.platform;
    }

    performUserValidationChecks() {
        // The timeout allows the UI to render properly before showing the box
        this.$timeout(() => {
            this.resetPasswordCheck(this.$rootScope.currentUser);
        }, 1000);
    }

    resetPasswordCheck(user: IUser) {
        if (!user) {
            return;
        }

        // Needs to be moved at some point, currently too many things depend on $rootScope.currentUser to move easily
        if (user.ResetPassword) {
            this.$mdDialog
                .show(this.$mdDialog
                    .alert()
                    .title(this.translationService.t("currentUserService.resetPasswordRequest"))
                    .textContent(this.translationService.t("currentUserService.resetPasswordRequestContent"))
                    .ok(this.translationService.t("currentUserService.changePassword"))
                )
                .then(() => {
                    this.accountService.showPasswordEditor(null, { forcePasswordReset: true });
                });
        }
    }

    getCurrentUserFromCache(): IUser {
        return this.cacheService.get(this.cacheName);
    }

    getCurrentUser(invalidateCache = false): angular.IPromise<IUser> {
        const currentUserPromise = this.getUser(invalidateCache);
        const cachedAccountUserMappings: IAccountUserMapping[] = this.sessionService.getItem(SettingsName.AccountUserMappings);
        const userResellerMappingsPromise = this.userResellerMappingService.getCurrentUserMappings();

        const accountUserMappingsPromise = (!invalidateCache && cachedAccountUserMappings?.length > 0)
            ? this.$q.resolve(cachedAccountUserMappings)
            : this.accountUserMappingService.getCurrentAccountUserMappings(invalidateCache);

    return this.$q
        .all([currentUserPromise, accountUserMappingsPromise, userResellerMappingsPromise])
        .then(([currentUser, accountMappings, userResellerMappings]) => {
            this.setSessionService(accountMappings);

            currentUser.AccountUserMappings = accountMappings.filter(mapping => mapping.UserId === currentUser.Id);

            return this.getCurrentAccountUserMapping(currentUser).then(currentAccountUserMapping => {
                currentUser.CurrentAccountUserMapping = currentAccountUserMapping;

                currentUser.ResellerIds = userResellerMappings
                    .filter(mapping => mapping.UserId === currentUser.Id && !mapping.EndTime)
                    .map(mapping => mapping.ResellerId);

                currentUser.platform = 
                    (currentUser.Type == UserType.InternalUser || currentUser.Type == UserType.Engineer) &&
                    !currentAccountUserMapping
                        ? UserPlatform.Foundation
                        : UserPlatform.Valkyrie;

                this.saveUserToCache(currentUser);

                return currentUser;
            });
        });
    }

    updateCurrentUserCache(user: IUser) {
        this.accountUserMappingService.updateCachedAccountUserMapping(user.Id, user.CurrentAccountUserMapping);
        this.cacheService.save(this.cacheName, user);

        this.$rootScope.currentUser = user;
        this.cachedUser = user;

        this.$rootScope.$broadcast(CurrentUserService.CurrentUserFeaturesUpdatedEvent);
    }

   
    private setSessionService(mappings: IAccountUserMapping[]): void {
        if (!this.accountUserMappingService.hasCachedAccountUserMappings()) {
            this.sessionService.setItem(SettingsName.AccountUserMappings, mappings);
        }
    }

    private getCurrentAccountUserMapping(user: IUser): angular.IPromise<IAccountUserMapping> {
        const mapping = user.AccountUserMappings?.length === 1 
            ? user.AccountUserMappings[0]
            : user.AccountUserMappings.find(x => x.AccountId == this.sessionService.getItem(SettingsName.AccountId));

        if (!mapping) {
            return this.$q.resolve(null);
        }

        return this.getCurrentAccountResellers().then(resellers => {
            mapping.resellers = resellers;
            return mapping;
        })
    }

    private saveUserToCache(user: IUser): void {
        this.cacheService.save(this.cacheName, user);
        this.$rootScope.currentUser = user;

        this.cachedUser = user;
    }

    private getUser(invalidateCache = false): angular.IPromise<IUser> {
        return this
            .$http({
                url: "/api/users/current",
                method: "GET",
                params: { minsToLive: 30, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IUser>) => {

                return response.data;
            });
    }

    // Due to AngularJS/Angular2 hybrid, needed to double this method
    // instead of using ResellerAccountMappingService
    private getCurrentAccountResellers(): angular.IPromise<IReseller[]> {
        return this
            .$http({
                url: `/api/foundation/resellermapping/account/current/resellers`,
                method: "GET",
                params: { minsToLive: 30 }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IReseller[]>) => {
                return response.data;
            });
    }
}
